import { Suspense, lazy, useMemo, useState } from 'react'
import { NotebookPen, PencilLine } from 'lucide-react'
import type { PartialBlock } from '@blocknote/core'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { blocksToPlainText } from '@/lib/blockText'
import { journalTradeName } from '../utils/format'
import type { JournalTrade } from '../types'

const RichNoteDialog = lazy(() =>
  import('@/components/notes/RichNoteDialog').then((m) => ({ default: m.RichNoteDialog })),
)

const JOURNAL_TAGS = ['Rule Followed', 'FOMO Entry', 'Revenge', 'Theta Day', 'Hedge Saved', 'Chop', 'Slippage Heavy'] as const

export function JournalReview({ trade, onPatched }: {
  trade: JournalTrade
  onPatched: (next: Partial<JournalTrade>) => void
}) {
  const [writerOpen, setWriterOpen] = useState(false)
  const [savingTags, setSavingTags] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const noteBlocks = useMemo(
    () => Array.isArray(trade.journalNote) ? trade.journalNote as PartialBlock[] : undefined,
    [trade.journalNote],
  )
  const notePreview = useMemo(
    () => blocksToPlainText(trade.journalNote).trim(),
    [trade.journalNote],
  )

  const toggleTag = async (tag: string): Promise<void> => {
    const next = trade.journalTags.includes(tag)
      ? trade.journalTags.filter((item) => item !== tag)
      : [...trade.journalTags, tag]
    // Tag clicks remain immediate; a failed save restores the previous review.
    onPatched({ journalTags: next })
    setSavingTags(true)
    setError(null)
    try {
      await api.patch(`/trades/journal/${trade.tradeId}`, { tags: next })
    } catch (e: unknown) {
      onPatched({ journalTags: trade.journalTags })
      setError(e instanceof Error ? e.message : 'The tags could not be saved.')
    } finally {
      setSavingTags(false)
    }
  }

  return (
    <section className="jn-panel jn-review" aria-label="Trade review">
      <header className="jn-panel-heading">
        <div className="flex items-center gap-2">
          <NotebookPen size={14} className="text-primary" />
          <h3>Review &amp; lesson</h3>
        </div>
        <span className="jn-badge">
          {notePreview || trade.journalTags.length || trade.reviewedAt ? 'Reviewed' : 'To review'}
        </span>
      </header>
      <div className="jn-review-body">
        {notePreview && <p className="jn-note-preview">{notePreview}</p>}
        <button
          type="button"
          onClick={() => setWriterOpen(true)}
          data-testid={`journal-write-${trade.tradeId}`}
          className="jn-write-button"
        >
          <PencilLine size={13} />{notePreview ? 'Edit lesson' : 'Write lesson'}
        </button>
        <div className="jn-review-tags">
          <h4>Review tags</h4>
          <div className="flex flex-wrap gap-1.5">
            {JOURNAL_TAGS.map((tag) => {
              const on = trade.journalTags.includes(tag)
              return (
                <button
                  key={tag}
                  type="button"
                  disabled={savingTags}
                  onClick={() => void toggleTag(tag)}
                  aria-pressed={on}
                  data-testid={`journal-tag-${tag.toLowerCase().replaceAll(' ', '-')}`}
                  className={cn('jn-tag', on && 'is-active')}
                >
                  {tag}
                </button>
              )
            })}
          </div>
        </div>
        {error && (
          <p role="alert" className="mt-2 text-[11px] text-destructive" data-testid="journal-save-error">
            {error}
          </p>
        )}
      </div>
      {writerOpen && (
        <Suspense fallback={null}>
          <RichNoteDialog
            open={writerOpen}
            onClose={() => setWriterOpen(false)}
            heading="Trade review"
            subject={`${journalTradeName(trade)} · ${trade.underlying}`}
            documentKey={trade.tradeId}
            initialContent={noteBlocks}
            placeholder="What did this trade teach you?"
            primaryLabel="Save lesson"
            onPersist={async ({ blocks }) => {
              await api.patch(`/trades/journal/${trade.tradeId}`, { note: blocks })
              onPatched({ journalNote: blocks })
            }}
          />
        </Suspense>
      )}
    </section>
  )
}
